import {gsap} from 'gsap';
import {IDimOrientation, LayoutDimData, ResizeHelper} from '../../core/utils/ResizeHelper';
import {BitmapTextStyleOptions, TextStyleOptions} from '../../core/setting/text/IText';
import {BitmapCountUp} from "@nolimitcity/slot-game/bin/component/BitmapCountUp";
import {Translator} from '../../core/setting/Translator';
import {TranslationTexts} from '../config/translate/TranslationTexts';
import {MathHelper} from '../../core/utils/MathHelper';
import {Helper} from '../../core/utils/Helper';
import {GameAssets} from '../config/texture/GameAssets';
import {PunkRocker} from "../PunkRocker";
import {SoundManager} from "../sound/SoundManager";
import {SoundConfig} from "../sound/SoundConfig";
import {FontStyle, FontWeight} from "@nolimitcity/slot-launcher/bin/loader/font/FontStatics";

export class PunkBitmapCountUp extends PIXI.Container {
    protected readonly counterDim: IDimOrientation = {
        landscape: {
            scale: new PIXI.Point(1, 1),
            position: new PIXI.Point(360, 300),
        },
        portrait: {
            scale: new PIXI.Point(1, 1),
            position: new PIXI.Point(360, 300)
        }
    };
    protected _countUp: BitmapCountUp;
    protected _countUp6x6: PIXI.Text;
    protected _waysStr6x6: PIXI.Text;
    protected _waysTxt: PIXI.Text;
    protected _timeline: gsap.core.Timeline;
    private readonly _6x6TextStyle: TextStyleOptions = {
        fontFamily: "Helvetica",
        fontStyle: FontStyle.ITALIC,
        fontSize: 126,
        fontWeight: FontWeight.EXTRA_BOLD,
        dropShadow: true,
        dropShadowAngle: 8,
        dropShadowBlur: 4,
        dropShadowDistance: 10,
        fill: "#ffe8d9",
        stroke: "#000000",
        strokeThickness: 10
    };

    private readonly _6x6WaysStyle: TextStyleOptions = {
        fontFamily: "Helvetica",
        fontStyle: FontStyle.ITALIC,
        fontSize: 40,
        fontWeight: FontWeight.EXTRA_BOLD,
        dropShadow: true,
        dropShadowAngle: 8,
        dropShadowBlur: 4,
        dropShadowDistance: 10,
        fill: "#ffe8d9",
        stroke: "#000000",
        strokeThickness: 2
    };

    private readonly _countUpStyle: BitmapTextStyleOptions = {
        fontName: GameAssets.BITMAP_FONT_WIN_COUNT_UP_LOW,
        fontSize: 68,
        align: "center",
        maxWidth: 580,
        letterSpacing: 5
    };
    private readonly _styleWays: TextStyleOptions = {
        dropShadow: true,
        dropShadowAngle: 7.5,
        fill: "white",
        fontFamily: "Helvetica",
        fontSize: 52,
        fontStyle: "italic",
        fontWeight: "bold",
        miterLimit: 0,
        strokeThickness: 3
    };
    private waysYOffset: number = 120;
    private waysTL: gsap.core.Timeline;

    constructor() {
        super();
        this.name = "counter";

        this.init();
    }

    public get dim(): LayoutDimData {
        return ResizeHelper.calcDim(this.counterDim);
    };

    public get timeRemaining(): number {
        return this._timeline && this._timeline.isActive() ? this._timeline.duration() - this._timeline.totalTime() : 0;
    }

    private set waysText(value: number) {
        this._waysTxt.text = value.toString() + " " + Translator.translate((value == 1 ? TranslationTexts.WAY_WON : TranslationTexts.WAYS_WON));
        this._waysTxt.updateTransform();
    }

    public play(from: number, to: number, duration: number, ways: number = 1, forToggle: boolean = false): gsap.core.Timeline {
        this.onResize();
        this._timeline = Helper.setTimeline(this._timeline!, new gsap.core.Timeline({onComplete: () => this.countUpAnimVal(to)}));

        const deciNumber: number = MathHelper.floorToDecimals(to, 2);

        this._timeline.to(this, {
            duration: duration,
            onStart: () => {
                this._countUp.getCountUpAnimation(from, deciNumber, duration, "power2.easeInOut");
            }
        }, 0);

        if (from == 0) {
            this._timeline.fromTo(this._countUp, {
                alpha: 0
            }, {
                duration: duration * 0.8,
                alpha: 1
            }, 0);
            if (duration)
                this._timeline.fromTo(this._countUp.scale, {
                    x: 0.88,
                    y: 0.88
                }, {
                    x: 1,
                    y: 1,
                    duration: duration,
                    ease: "power2.in"
                }, 0);
        } else {
            this._countUp.alpha = 1;
            this._countUp.scale.set(1);
        }

        this.waysText = ways;

        return this._timeline;
    }

    public triggerCountUp6x6(from: number, to: number, duration: number): gsap.core.Timeline {
        this.onResize();
        this._timeline = Helper.setTimeline(this._timeline!, new gsap.core.Timeline());

        // #349 -> 2nd point
        const tl: gsap.core.Timeline = gsap.timeline({
            onStart: () => {
                this._countUp6x6.text = Math.round(Number(this._countUp6x6.text)).toString();
                SoundManager.playSoundEffect(SoundConfig.COUNTER_6X6, -1);
            },
            onUpdate: () => {
                this._countUp6x6.text = Math.round(Number(this._countUp6x6.text)).toString();
            },
            onComplete: () => {
                this._countUp6x6.text = Math.round(to).toString();

                SoundManager.stopSoundEffect(SoundConfig.COUNTER_6X6);
            }
        });
        tl.fromTo(this._countUp6x6, {text: from + ""}, {text: to + "", duration: duration});

        this._timeline.add(tl, 0);

        if (from == 0) {
            this._timeline.fromTo([this._countUp6x6, this._waysStr6x6], {
                alpha: 0
            }, {
                duration: duration * 0.8,
                alpha: 1
            }, 0);
            if (duration)
                this._timeline.fromTo(this._countUp6x6.scale, {
                    x: 0.88,
                    y: 0.88
                }, {
                    x: 1,
                    y: 1,
                    duration: duration,
                    ease: "power2.in"
                }, 0);
        } else {
            this._countUp6x6.alpha = 1;
            this._waysStr6x6.alpha = 1;
            this._countUp6x6.scale.set(1);
        }

        return this._timeline;
    }

    public showWays(duration: number = 0.65, resetPos?: boolean): void {
        this.waysTL = Helper.setTimeline(this.waysTL, new gsap.core.Timeline(), true);
        this.waysTL.fromTo(this._waysTxt, {alpha: 0}, {alpha: 1, duration: duration}, 0)
        this.waysTL.fromTo(this._waysTxt, {y: this.waysYOffset - 40}, {y: this.waysYOffset, duration: duration, ease: "back.out(1.7)"}, 0)

    }

    public stop(endValue?: number, duration: number = 0.5, delay: number = 1, useLastAlpha: boolean = false): gsap.core.Timeline {
        this._timeline = Helper.setTimeline(this._timeline!, new gsap.core.Timeline());
        this.waysTL = Helper.setTimeline(this.waysTL);

        if (this._countUp.alpha == 0) {
            return this._timeline;
        }

        let startAlpha: number = useLastAlpha ? this._countUp.alpha : 1,
            startAlphaWays: number = useLastAlpha ? this._waysTxt.alpha : 1;

        const isAutoPlay: boolean = PunkRocker.autoPlayPlugin.isAutoplayRound,
            isBonusGame: boolean = PunkRocker.model.gameData.isFreeSpin;

        if (!(isAutoPlay || isBonusGame)) {
            // #349 -> 3rd point
            if (endValue) {
                this._countUp.getCountUpAnimation(endValue, endValue, duration);
            }

            this._timeline.fromTo(this._countUp, {
                alpha: startAlpha
            }, {
                delay: delay,
                duration: duration,
                alpha: 0
            }, 0);
        }

        this._timeline.fromTo([this._waysTxt, this._countUp], {alpha: startAlphaWays}, {delay: delay, duration: duration, alpha: 0}, 0);
        this._timeline.play(0.001);
        return this._timeline;
    }

    public hide(): void {
        if (this._countUp.alpha === 0) {
            return;
        }

        const tl: gsap.core.Timeline = new gsap.core.Timeline();
        tl.to(this._countUp, {
            duration: 0.2,
            alpha: 0
        });
    }

    public hideExpandCountUp(): void {
        if (this._countUp6x6.alpha === 0) {
            return;
        }

        const tl: gsap.core.Timeline = new gsap.core.Timeline();
        tl.to([this._countUp6x6, this._waysStr6x6], {
            duration: 0.2,
            alpha: 0
        });
    }

    public onResize(): void {
        this.position = this.dim.position;
        this.scale = this.dim.scale;
    }

    public remove(): gsap.core.Timeline {
        const tl: gsap.core.Timeline = gsap.timeline(),
            isAutoPlay: boolean = PunkRocker.autoPlayPlugin.isAutoplayRound,
            isBonusGame: boolean = PunkRocker.model.gameData.isFreeSpin;

        if (this._countUp.visible) {
            if (!(isAutoPlay || isBonusGame)) {
                tl.to(this, {
                    duration: 0.2
                });
            }
            tl.to([this._countUp, this._waysTxt], {
                duration: 0.1,
                alpha: 0,
                onComplete: () => {
                    this._countUp.text = "";
                    this._waysTxt.text = "";
                }
            });
        }
        return tl;
    }

    protected init(): void {
        this._waysTxt = this.addChild(new PIXI.Text(""));
        this._waysTxt.alpha = 0;
        this._waysTxt.style = this._styleWays;
        this._waysTxt.anchor.set(0.5, 0.5);
        this._waysTxt.position.set(0, 180);

        this._countUp = new BitmapCountUp(this._countUpStyle);
        this._countUp.shrinkSize = true;
        this._countUp.alpha = 0;
        this.addChild(this._countUp);

        this._countUp6x6 = new PIXI.Text("");
        this._countUp6x6.style = this._6x6TextStyle;
        this._countUp6x6.anchor.set(0.5, 0.5);
        this._countUp6x6.position.set(0, -100);
        this._countUp6x6.alpha = 0;
        this.addChild(this._countUp6x6);

        this._waysStr6x6 = new PIXI.Text(Translator.translate(TranslationTexts.BANNER_WAYS));
        this._waysStr6x6.style = this._6x6WaysStyle;
        this._waysStr6x6.anchor.set(0.5, 0.5);
        this._waysStr6x6.alpha = 0;
        this.addChild(this._waysStr6x6);

        this.onResize();
    }

    private countUpAnimVal(val: number): void {
        this._countUp.getCountUpAnimation(val, val, 0.001);
    }
}
